import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Plus, X } from 'lucide-react';

const ServiceModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  service = null, 
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    title: service?.title || '',
    description: service?.description || '',
    icon: service?.icon || '🔧',
    image: service?.image || '',
    price: service?.price || '',
    features: service?.features || [''],
    order: service?.order || 0,
    isActive: service?.isActive !== undefined ? service.isActive : true
  });

  const handleSave = () => {
    if (!formData.title || !formData.description) {
      return;
    }

    const serviceData = {
      ...formData,
      features: formData.features.filter(f => f.trim() !== ''),
      order: parseInt(formData.order) || 0
    };

    onSave(serviceData);
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const updateFeature = (index, value) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map((f, i) => i === index ? value : f)
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 text-white border-amber-500/20 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-amber-400">
            {service ? 'עריכת שירות' : 'הוספת שירות חדש'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">שם השירות*</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                placeholder="קנייה ומכירה"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">אייקון</label>
              <Input
                value={formData.icon}
                onChange={(e) => setFormData(prev => ({...prev, icon: e.target.value}))}
                placeholder="🔧"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">תיאור השירות*</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
              placeholder="תיאור מפורט של השירות..."
              className="bg-gray-700 border-gray-600 text-white min-h-[100px]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">תמונה</label>
              <Input
                value={formData.image}
                onChange={(e) => setFormData(prev => ({...prev, image: e.target.value}))}
                placeholder="קישור לתמונה"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">מחיר</label>
              <Input
                value={formData.price}
                onChange={(e) => setFormData(prev => ({...prev, price: e.target.value}))}
                placeholder="החל מ-5,000 ₪"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">תכונות השירות</label>
            <div className="space-y-2">
              {formData.features.map((feature, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={feature}
                    onChange={(e) => updateFeature(index, e.target.value)}
                    placeholder="תכונה..."
                    className="bg-gray-700 border-gray-600 text-white flex-1"
                  />
                  {formData.features.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeFeature(index)}
                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addFeature}
                className="border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black"
              >
                <Plus className="w-4 h-4 ml-2" />
                הוסף תכונה
              </Button>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              onClick={handleSave}
              disabled={loading || !formData.title || !formData.description}
              className="bg-amber-500 hover:bg-amber-600 text-black flex-1"
            >
              {loading ? 'שומר...' : service ? 'עדכן שירות' : 'הוסף שירות'}
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="border-gray-600 text-white hover:bg-gray-700"
            >
              ביטול
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceModal;